import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, output, Signal, ViewChild } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { FormControl, FormGroup } from '@angular/forms';
import { TextFieldModule } from '@angular/cdk/text-field';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';
import { IconComponent } from '@outlaw/icons';
import { SnackbarService } from '../../../services/util/snackbar.service';
import { isValidPostLink } from '../../../utils/post-link.util';
import { ImageGridComponent } from '../image-grid/image-grid.component';
import { FieldErrorComponent } from '../../field-error/field-error.component';
import { FloatingInputComponent } from '../../floating-input/floating-input.component';
import { SelectionsMenuComponent } from '../../selections-menu/selections-menu.component';
import { AddPostStateService } from '../add-post-state.service';

const MAX_IMAGES = 10;

@Component({
  selector: 'outlaw-post-tab',
  templateUrl: './post-tab.component.html',
  styleUrls: ['./post-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, TextFieldModule, IconComponent, ImageGridComponent, FieldErrorComponent, FloatingInputComponent, SelectionsMenuComponent],
})
export class PostTabComponent {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  state = inject(AddPostStateService);
  #snackbar = inject(SnackbarService);
  #formChanged: Signal<unknown>;

  form = input.required<FormGroup<{
    text: FormControl<string>;
    linkTitle: FormControl<string>;
    linkUrl: FormControl<string>;
  }>>();

  publishRequested = output<void>();

  canPublish: Signal<boolean>;

  constructor() {
    this.#formChanged = toSignal(
      toObservable(this.form).pipe(switchMap(f => f.valueChanges)),
      { initialValue: null },
    );
    this.canPublish = computed(() => {
      this.#formChanged();
      const text = this.form().getRawValue().text.trim();
      return (text.length > 0 || this.state.images().length > 0 || this.state.links().length > 0)
        && !this.state.isPublishing() && !this.state.isUploading();
    });
  }

  onAddMedia(): void {
    if (this.state.images().length >= MAX_IMAGES) {
      this.#snackbar.show({ message: `Maximum ${MAX_IMAGES} images allowed` });
      return;
    }
    this.fileInput?.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files || files.length === 0) return;
    this.state.uploadImages(Array.from(files));
    input.value = '';
  }

  onImagesReordered(images: import('../../../interfaces/community-item').Image[]): void {
    this.state.reorderImages(images);
  }

  onImageRemoved(index: number): void {
    this.state.removeImage(index);
  }

  onAddLink(): void {
    this.state.toggleLinkInput();
    this.form().patchValue({ linkUrl: '', linkTitle: '' });
  }

  confirmLink(): void {
    const { linkUrl, linkTitle } = this.form().getRawValue();
    const url = linkUrl.trim();
    const title = linkTitle.trim();
    if (!url) return;

    if (!isValidPostLink(url)) {
      this.#snackbar.show({ message: 'Please enter a valid URL (e.g. https://example.com)' });
      return;
    }

    this.state.addLink({ link_url: url, link_text: title || url });
    this.state.showLinkInput.set(false);
    this.form().patchValue({ linkUrl: '', linkTitle: '' });
  }

  removeLink(index: number): void {
    this.state.removeLink(index);
  }
}
